import React, { useState, useEffect } from 'react';
import { Table, Space, Select, Row, Col, Button, DatePicker, Input, Form, message, Tabs } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { getConsultantBillingLoans, getConsultants, updateConsultantBilling, updateTemporaryBilling } from '../services/loanService';
import locale from 'antd/es/date-picker/locale/de_DE';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Page from '../components/layout/Page';
import MetricCard from '../components/MetricCard';
import { Link, useNavigate, useParams } from 'react-router-dom';

const { Option } = Select;

const ConsultantBilling = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { consultantId } = useParams();

    const [selectedConsultant, setSelectedConsultant] = useState(null);
    const [selectedConsultantName, setSelectedConsultantName] = useState('');
    const [consultants, setConsultants] = useState([]);
    const [primaryLoans, setPrimaryLoans] = useState([]);
    const [temporaryLoans, setTemporaryLoans] = useState([]);
    const [withdrawalLoans, setWithdrawalLoans] = useState([]);
    const [selectedPrimaryLoans, setSelectedPrimaryLoans] = useState([]);
    const [selectedTemporaryLoans, setSelectedTemporaryLoans] = useState([]);
    const [selectedWithdrawalLoans, setSelectedWithdrawalLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState({
        totalVolume: 0,
        totalProvision: 0,
        totalIncentive: 0,
        totalTemporaryShare: 0,
        totalExtraBonus: 0,
        totalWithdrawalShare: 0,
        totalSum: 0
    });

    const primaryColumns = [
        {
            title: t('loan.fields.primaryConsultant'),
            key: 'primary_consultant',
            render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
        },      
        {
            title: t('loan.fields.temporaryConsultant'),
            key: 'secondary_consultant',
            render: (_, record) => record.secondary_consultant ? 
                `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
        },      
        {
            title: t('loan.fields.customer'),
            key: 'customer',
            render: (_, record) => record.customer?.name || ''
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },
        {
            title: t('Provision'),
            dataIndex: 'provision',
            key: 'provision',
            //render with comma and 8 decimal places
            render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
            align: 'right'
        },  
        {
            title: t('loan.fields.extraBonus'),
            key: 'extra_bonus',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.extra_bonus).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },          
        {
            title: t('consultantBilling.primary_share'),
            key: 'commission',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.primary_share).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },   
    ];

    const temporaryColumns = [
        {
            title: t('loan.fields.primaryConsultant'),
            key: 'primary_consultant',
            render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
        },      
        {
            title: t('loan.fields.temporaryConsultant'),
            key: 'secondary_consultant',
            render: (_, record) => record.secondary_consultant ? 
                `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
        },      
        {
            title: t('loan.fields.customer'),
            key: 'customer',
            render: (_, record) => record.customer?.name || ''
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'evaluation_amount',
            key: 'evaluation_amount',
            render: (evaluation_amount) => evaluation_amount ? `${Number(evaluation_amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },        
        {
            title: t('Provision'),
            dataIndex: 'provision',
            key: 'provision',
            //render with comma and 8 decimal places
            render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
            align: 'right'
        },             
        {
            title: t('consultantBilling.temporary_share'),
            key: 'temporary_share',
            render: (_, record) => record.temporary_consultant_share ? 
                `${Number(record.temporary_consultant_share).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        }
    ];

    const withdrawalColumns = [
        {
            title: t('loan.fields.primaryConsultant'),
            key: 'primary_consultant',
            render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
        },      
        {
            title: t('loan.fields.temporaryConsultant'),
            key: 'secondary_consultant',
            render: (_, record) => record.secondary_consultant ? 
                `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
        },      
        {
            title: t('loan.fields.customer'),
            key: 'customer',
            render: (_, record) => record.customer?.name || ''
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },
        {
            title: t('Provision'),
            dataIndex: 'provision',
            key: 'provision',
            //render with comma and 8 decimal places
            render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
            align: 'right'
        },  
        {
            title: t('loan.fields.extraBonus'),
            key: 'extra_bonus',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.extra_bonus).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },          
        {
            title: t('loan.fields.primaryShare'),
            key: 'commission',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.primary_share).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },   
    ];


    const calculateMetrics = (primary, temporary, withdrawal) => {
        const selectedPrimaryData = primary.filter(loan => selectedPrimaryLoans.includes(loan.id));
        const selectedTemporaryData = temporary.filter(loan => selectedTemporaryLoans.includes(loan.id));
        const selectedWithdrawalData = withdrawal.filter(loan => selectedWithdrawalLoans.includes(loan.id));
        
        // Calculate totals
        const totalPrimaryShare = selectedPrimaryData.reduce((sum, loan) => {
            const primaryShare = parseFloat(loan.commission_info?.primary_share) || 0;
            return sum + primaryShare;
        }, 0);
        const totalExtraBonus = selectedPrimaryData.reduce((sum, loan) => {
            const extraBonus = parseFloat(loan.commission_info?.extra_bonus) || 0;
            return sum + extraBonus;
        }, 0);
        const totalTemporaryShare = selectedTemporaryData.reduce((sum, loan) => {
            const temporaryShare = parseFloat(loan.temporary_consultant_share) || 0;
            return sum + temporaryShare;
        }, 0);
        const totalWithdrawalShare = selectedWithdrawalData.reduce((sum, loan) => {
            const withdrawalShare = parseFloat(loan.commission_info?.primary_share) || 0;
            const extraBonus = parseFloat(loan.commission_info?.extra_bonus) || 0;
            const temporaryShare = parseFloat(loan.temporary_consultant_share) || 0;
            return sum + withdrawalShare + extraBonus + temporaryShare;
        }, 0);

        const totalSum = totalPrimaryShare + totalExtraBonus + totalTemporaryShare + totalWithdrawalShare;

        setMetrics({
            totalPrimaryShare,
            totalExtraBonus,
            totalTemporaryShare,
            totalWithdrawalShare,
            totalSum
        });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            if (selectedConsultant) {
                const loansData = await getConsultantBillingLoans(selectedConsultant);
                
                // Split loans by consultant billable status
                const primary = loansData.filter(loan => loan.consultant_billable_status === 'primary');
                const temporary = loansData.filter(loan => loan.consultant_billable_status === 'temporary');
                const withdrawal = loansData.filter(loan => loan.consultant_billable_status === 'withdrawal');
                
                setPrimaryLoans(primary);
                setTemporaryLoans(temporary);
                setWithdrawalLoans(withdrawal);

                // Calculate initial metrics
                calculateMetrics(primary, temporary, withdrawal);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const consultantsData = await getConsultants();
                setConsultants(consultantsData);
                
                // Set selected consultant after consultants are loaded
                if (consultantId && consultantsData.length > 0) {
                    const consultant = consultantsData.find(c => c.id === parseInt(consultantId));
                    if (consultant) {
                        setSelectedConsultant(consultant.id);
                        setSelectedConsultantName(`${consultant.first_name} ${consultant.last_name}`);
                    }
                }
            } catch (error) {
                console.error('Error loading consultants:', error);
                message.error('Fehler beim Laden der Berater');
            }
        };

        loadInitialData();
    }, [consultantId]);

    useEffect(() => {
        if (selectedConsultant) {
            fetchData();
        }
    }, [selectedConsultant]);

    useEffect(() => {
        if (primaryLoans.length > 0 || temporaryLoans.length > 0 || withdrawalLoans.length > 0) {
            calculateMetrics(primaryLoans, temporaryLoans, withdrawalLoans);
        }
    }, [selectedPrimaryLoans, selectedTemporaryLoans, selectedWithdrawalLoans]);

    const handleConsultantChange = (value) => {
        const consultant = consultants.find(c => c.id === value);
        setSelectedConsultant(value);
        if (consultant) {
            setSelectedConsultantName(`${consultant.first_name} ${consultant.last_name}`);
        }
        setSelectedPrimaryLoans([]); 
        setSelectedTemporaryLoans([]); 
        setSelectedWithdrawalLoans([]); 
        // Update URL when consultant changes
        navigate(`/consultant-billing/${value}`);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const { invoiceDate, invoiceNumber } = values;

            const billingData = {
                billingDate: invoiceDate.format('YYYY-MM-DD'),
                billingNumber: invoiceNumber
            };

            // Update primary consultant loans
            const primaryUpdates = selectedPrimaryLoans.map(loanId =>
                updateConsultantBilling(loanId, billingData)
            );

            // Update temporary consultant loans
            const temporaryUpdates = selectedTemporaryLoans.map(loanId =>
                updateTemporaryBilling(loanId, billingData)
            );

            // Update withdrawal loans
            const withdrawalUpdates = selectedWithdrawalLoans.map(loanId =>
                updateConsultantBilling(loanId, billingData)
            );

            // Wait for all updates to complete
            await Promise.all([...primaryUpdates, ...temporaryUpdates, ...withdrawalUpdates]);
            
            message.success('Beraterabrechnung erfolgreich gespeichert');
            
            // Reset form and selection
            form.resetFields();
            setSelectedPrimaryLoans([]);
            setSelectedTemporaryLoans([]);
            setSelectedWithdrawalLoans([]);
            fetchData();
        } catch (error) {
            console.error('Error updating consultant billing:', error);
            message.error('Fehler beim Speichern der Beraterabrechnung');
        }
    };

    const metricCards = [
        {
            title: t('consultantBilling.totalSum'),
            value: `${Number(metrics.totalSum).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.primary_consultant_loans'),
            value: `${Number(metrics.totalPrimaryShare).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.temporary_consultant_loans'),
            value: `${Number(metrics.totalTemporaryShare).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.extraBonus'),
            value: `${Number(metrics.totalExtraBonus).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.withdrawal_loans'),
            value: `${Number(metrics.totalWithdrawalShare).toLocaleString('de-DE')} €`,
            precision: 2
        }
    ];

    const handlePrimaryLoansSelect = (selectedRowKeys) => {
        setSelectedPrimaryLoans(selectedRowKeys);
    };

    const handleTemporaryLoansSelect = (selectedRowKeys) => {
        setSelectedTemporaryLoans(selectedRowKeys);
    };

    const handleWithdrawalLoansSelect = (selectedRowKeys) => {
        setSelectedWithdrawalLoans(selectedRowKeys);
    };

    return (
        <Page 
            title={t('consultantBilling.title')}
            subtitle={t('consultantBilling.subtitle')}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('billing.selectConsultant')}
                            onChange={handleConsultantChange}
                            value={selectedConsultant}
                        >
                            {consultants.map(consultant => {
                                const name = `${consultant.first_name} ${consultant.last_name}`;
                                return (
                                    <Select.Option key={consultant.id} value={consultant.id} title={name}>
                                        {name}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Col>
                </Row>

                {selectedConsultant && (
                    <>
                        <Row gutter={[16, 16]}>
                            {metricCards.map((card, index) => (
                                <Col key={index} xs={24} sm={12} md={6}>
                                    <MetricCard {...card} />
                                </Col>
                            ))}
                        </Row>

                        <Form
                            form={form}
                            layout="vertical"
                            style={{ width: '100%' }}
                        >
                            <Row gutter={24}>
                                <Col xs={24} sm={24} md={8} lg={8}>
                                    <Form.Item
                                        name="invoiceDate"
                                        label={t('common.invoiceDate')}
                                        rules={[{ required: true, message: t('common.validation.required') }]}
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD.MM.YYYY"
                                            locale={locale}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={8}>
                                    <Form.Item
                                        name="invoiceNumber"
                                        label={t('common.invoiceNumber')}
                                        rules={[{ required: true, message: t('common.validation.required') }]}
                                    >
                                        <Input style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={8} style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 24 }}>
                                    <Button
                                        type="primary"
                                        onClick={handleSubmit}
                                        disabled={selectedPrimaryLoans.length === 0 && selectedTemporaryLoans.length === 0 && selectedWithdrawalLoans.length === 0}
                                        style={{ width: '100%' }}
                                    >
                                        {t('consultantBilling.markBilled')}
                                    </Button>
                                </Col>
                            </Row>                            
                        </Form>

                        <div style={{ marginTop: 24 }}>
                            <h2>{t('billing.primary_consultant_loans')}</h2>
                            <div style={{ 
                                width: '100%',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }}>
                                <Table
                                    rowSelection={{
                                        selectedRowKeys: selectedPrimaryLoans,
                                        onChange: handlePrimaryLoansSelect
                                    }}
                                    dataSource={primaryLoans}
                                    columns={primaryColumns}
                                    rowKey="id"
                                    loading={loading}
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: 24 }}>
                            <h2>{t('billing.temporary_consultant_loans')}</h2>
                            <div style={{ 
                                width: '100%',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }}>
                                <Table
                                    rowSelection={{
                                        selectedRowKeys: selectedTemporaryLoans,
                                        onChange: handleTemporaryLoansSelect
                                    }}
                                    dataSource={temporaryLoans}
                                    columns={temporaryColumns}
                                    rowKey="id"
                                    loading={loading}
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: 24 }}>
                            <h2>{t('billing.withdrawal_loans')}</h2>
                            <div style={{ 
                                width: '100%',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }}>
                                <Table
                                    rowSelection={{
                                        selectedRowKeys: selectedWithdrawalLoans,
                                        onChange: handleWithdrawalLoansSelect
                                    }}
                                    dataSource={withdrawalLoans}
                                    columns={withdrawalColumns}
                                    rowKey="id"
                                    loading={loading}
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                        </div>                        
                    </>
                )}
            </Space>
        </Page>
    );
};

export default ConsultantBilling;
